<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-vue-next";
import AnimatedSection from "./AnimatedSection.vue";

const { t } = useI18n();

const plans = computed(() => [
  {
    key: "basic",
    name: t("landing.pricing.plans.basic.name"),
    description: t("landing.pricing.plans.basic.description"),
    price: t("landing.pricing.plans.basic.price"),
    period: t("landing.pricing.plans.basic.period"),
    features: [
      t("landing.pricing.plans.basic.features.monitoring"),
      t("landing.pricing.plans.basic.features.reports"),
      t("landing.pricing.plans.basic.features.support"),
    ],
    highlighted: false,
  },
  {
    key: "pro",
    name: t("landing.pricing.plans.pro.name"),
    description: t("landing.pricing.plans.pro.description"),
    price: t("landing.pricing.plans.pro.price"),
    period: t("landing.pricing.plans.pro.period"),
    features: [
      t("landing.pricing.plans.pro.features.basic_included"),
      t("landing.pricing.plans.pro.features.advanced_alerts"),
      t("landing.pricing.plans.pro.features.ai_anomalies"),
      t("landing.pricing.plans.pro.features.priority_support"),
    ],
    highlighted: true,
  },
  {
    key: "enterprise",
    name: t("landing.pricing.plans.enterprise.name"),
    description: t("landing.pricing.plans.enterprise.description"),
    price: t("landing.pricing.plans.enterprise.price"),
    period: t("landing.pricing.plans.enterprise.period"),
    features: [
      t("landing.pricing.plans.enterprise.features.integrations"),
      t("landing.pricing.plans.enterprise.features.dedicated_support"),
      t("landing.pricing.plans.enterprise.features.compliance"),
      t("landing.pricing.plans.enterprise.features.deployment"),
    ],
    highlighted: false,
  },
]);
</script>

<template>
  <section id="pricing" class="py-24 bg-secondary/20 relative scroll-mt-20">
    <!-- Background pattern -->
    <div class="absolute inset-0 cyber-grid opacity-30" />
    
    <div class="container mx-auto px-6 relative z-10">
      <!-- Section header -->
      <AnimatedSection animation="fade-up" class="text-center mb-16 max-w-2xl mx-auto">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-6">
          {{ t("landing.pricing.title") }}
        </h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.pricing.subtitle") }}
        </p>
      </AnimatedSection>

      <!-- Pricing cards -->
      <div class="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
        <AnimatedSection 
          v-for="(plan, index) in plans"
          :key="index"
          animation="fade-up"
          :delay="index * 150"
        >
          <div 
            :class="[
              'relative rounded-xl p-6 lg:p-8 border transition-all duration-300 h-full',
              plan.highlighted 
                ? 'bg-card border-accent/40 cyan-border-glow scale-105 z-10' 
                : 'bg-card/50 border-border/40 hover:border-border/60'
            ]"
          >
            <!-- Highlighted badge -->
            <div v-if="plan.highlighted" class="absolute -top-3 left-1/2 -translate-x-1/2">
              <span class="px-4 py-1 text-xs font-medium bg-accent text-accent-foreground rounded-full">
                {{ t("landing.pricing.recommended_badge") }}
              </span>
            </div>

            <!-- Plan name -->
            <h3 class="text-xl font-bold text-foreground mb-2">
              {{ plan.name }}
            </h3>

            <!-- Description -->
            <p class="text-sm text-muted-foreground mb-6 min-h-[48px]">
              {{ plan.description }}
            </p>

            <!-- Price -->
            <div class="mb-6">
              <span class="text-3xl font-bold text-foreground">
                {{ plan.price }}
              </span>
              <span class="text-sm text-muted-foreground">
                {{ plan.period }}
              </span>
            </div>

            <!-- CTA Button -->
            <Button 
              :variant="plan.highlighted ? 'hero-primary' : 'titanium'" 
              class="w-full mb-6"
            >
              {{ plan.key === "enterprise" ? t("landing.pricing.cta.contact") : t("landing.pricing.cta.start_now") }}
            </Button>

            <!-- Features -->
            <ul class="space-y-3">
              <li v-for="(feature, i) in plan.features" :key="i" class="flex items-start gap-3 text-sm">
                <Check :class="['w-4 h-4 mt-0.5 flex-shrink-0', plan.highlighted ? 'text-accent' : 'text-muted-foreground']" />
                <span class="text-muted-foreground">{{ feature }}</span>
              </li>
            </ul>
          </div>
        </AnimatedSection>
      </div>
    </div>
  </section>
</template>
